import { useCallback, useMemo } from "react";
import { useMutation, useQueries, useQuery } from "@tanstack/react-query";
import { api } from "api";
import { useCombinedState } from "hooks/useCombinedState";
import { useFetchStateHelper } from "hooks/useFetchState";

const useActionMedia = ({ actionId, fieldId }) => {
  const { data: media, ...queryResponse } = useQuery({
    queryKey: ["fetch-action-media", actionId],
    queryFn: () => api.actions.media.fetchActionMedia({ actionId, fieldId }),
  });
  const { fetchState } = useFetchStateHelper(queryResponse);

  const { state: allMedia, setState } = useCombinedState(media);

  const addToMediaState = useCallback(
    (file) => {
      setState((prev) => [file, ...prev]);
    },
    [setState],
  );

  const removeFromMediaState = useCallback(
    (fileId) => {
      setState((prev) => prev.filter((file) => file.id !== fileId));
    },
    [setState],
  );

  const updateMediaState = useCallback(
    (fileId, data) => {
      setState((prev) => {
        if (!prev) return [];

        if (!fileId || !data) return prev;

        // Update the media item with the matching id
        if (prev.some((file) => file.id === fileId)) {
          return prev.map((file) =>
            file.id === fileId ? { ...file, ...data } : file,
          );
        }

        // If no match found, return the previous state unchanged
        return prev;
      });
    },
    [setState],
  );

  return {
    media: allMedia,
    addToMediaState,
    removeFromMediaState,
    updateMediaState,
    fetchState,
  };
};

const useDeleteActionMedia = ({ fieldId }) => {
  const { mutateAsync, ...queryResponse } = useMutation({
    queryKey: ["delete-media"],
    mutationFn: (fileId) => api.actions.media.delete({ fieldId, fileId }),
  });

  const { fetchState } = useFetchStateHelper(queryResponse);

  return { deleteMedia: mutateAsync, fetchState };
};

const useUploadActionMedia = ({ actionId, fieldId }) => {
  const { mutateAsync: uploadMedia, ...queryResponse } = useMutation({
    queryKey: ["upload-media"],
    mutationFn: (payload) =>
      api.actions.media.upload({ actionId, fieldId, payload }),
  });

  const { fetchState } = useFetchStateHelper(queryResponse);

  return { uploadMedia, fetchState };
};

const useUpdateActionMedia = ({ fieldId }) => {
  const { mutateAsync: patchMedia, ...queryResponse } = useMutation({
    queryKey: ["update-media"],
    mutationFn: ({ fileId, payload }) =>
      api.actions.media.update({ fileId, fieldId, payload }),
  });

  const { fetchState } = useFetchStateHelper(queryResponse);

  return { patchMedia, fetchState };
};

const useFetchAttachments = ({ fieldId }) => {
  const { data: attachments, ...queryResponse } = useQuery({
    queryKey: ["fetch-actions-attachments", fieldId],
    queryFn: () => api.actions.media.fetchFieldMedia({ fieldId }),
    enabled: Boolean(fieldId),
  });
  const { fetchState } = useFetchStateHelper(queryResponse);

  return { attachments, fetchState };
};

const useDownloadActionAttachments = ({ url }) => {
  const { data: downloadedAttachment, ...queryResponse } = useQuery({
    queryKey: ["downloaded-action-attachment", url],
    queryFn: () => api.actions.media.download(url),
    enabled: Boolean(url),
    staleTime: Infinity,
  });

  const { fetchState } = useFetchStateHelper(queryResponse);

  return { downloadedAttachment, fetchState };
};

const useDownloadActionAttachmentsList = ({ urls = [] }) => {
  const uniqueUrls = useMemo(() => {
    const safeUrls = (urls ?? [])
      .filter((url) => typeof url === "string")
      .map((url) => url.trim())
      .filter(Boolean);

    return Array.from(new Set(safeUrls));
  }, [urls]);

  const queryResults = useQueries({
    queries: uniqueUrls.map((url) => ({
      queryKey: ["downloaded-action-attachment", url],
      queryFn: () => api.actions.media.download(url),
      enabled: Boolean(url),
      staleTime: Infinity,
    })),
  });

  const downloadedAttachmentsByUrl = useMemo(
    () =>
      uniqueUrls.reduce((acc, url, index) => {
        acc[url] = queryResults[index]?.data ?? null;
        return acc;
      }, {}),
    [queryResults, uniqueUrls],
  );

  const loadingByUrl = useMemo(
    () =>
      uniqueUrls.reduce((acc, url, index) => {
        const queryResult = queryResults[index];
        acc[url] = Boolean(
          queryResult?.isLoading ||
            queryResult?.isFetching ||
            queryResult?.isPending,
        );
        return acc;
      }, {}),
    [queryResults, uniqueUrls],
  );

  return { downloadedAttachmentsByUrl, loadingByUrl, queryResults };
};

export {
  useActionMedia,
  useDeleteActionMedia,
  useDownloadActionAttachments,
  useDownloadActionAttachmentsList,
  useUploadActionMedia,
  useUpdateActionMedia,
  useFetchAttachments,
};
